import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import VerifiedIcon from "@mui/icons-material/Verified";
import PasswordIcon from "@mui/icons-material/Password";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

const AuthAdmin = () => {
  const [email, setEmail] = React.useState("");
  const [verifyCode, setVerifyCode] = React.useState("");
  const [oldPwd, setOldPwd] = React.useState("");
  const [newPwd, setNewPwd] = React.useState("");
  const [resetToken, setResetToken] = React.useState("");
  const [resetNewPwd, setResetNewPwd] = React.useState("");
  const notify = useNotify();

  const requestReset = async () => {
    if (!email.trim()) return;
    try {
      await httpClient(`${API_BASE_URL}/auth/request-reset`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      notify("Email de réinitialisation envoyé", { type: "success" });
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  const doReset = async () => {
    if (!resetToken.trim() || !resetNewPwd.trim()) return;
    try {
      await httpClient(`${API_BASE_URL}/auth/reset`, {
        method: "POST",
        // ResetPassword : le service exige matchingPassword === password
        body: JSON.stringify({
          token: resetToken,
          password: resetNewPwd,
          matchingPassword: resetNewPwd,
        }),
      });
      notify("Mot de passe réinitialisé", { type: "success" });
      setResetToken(""); setResetNewPwd("");
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  const verifyEmail = async () => {
    if (!email.trim() || !verifyCode.trim()) return;
    try {
      // email & code sont des @RequestParam (pas de body JSON)
      const qs = new URLSearchParams({ email, code: verifyCode });
      await httpClient(`${API_BASE_URL}/auth/verify-email?${qs}`, {
        method: "POST",
      });
      notify("Email vérifié", { type: "success" });
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  const changePwd = async () => {
    if (!email.trim() || !oldPwd || !newPwd) return;
    try {
      await httpClient(`${API_BASE_URL}/auth/changePassword`, {
        method: "POST",
        // ResetPassword : password = ancien mot de passe,
        // matchingPassword = nouveau (AuthenticationService.changePassword)
        body: JSON.stringify({ email, password: oldPwd, matchingPassword: newPwd }),
      });
      notify("Mot de passe modifié", { type: "success" });
      setOldPwd(""); setNewPwd("");
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <LockResetIcon /> Outils d'authentification
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Outils admin sensibles. Toute opération est tracée dans les audit logs.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <LockResetIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Demander une réinitialisation de mot de passe
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="email" label="Email du compte"
              value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button variant="contained" onClick={requestReset}
              disabled={!email.trim()}>
              Envoyer le mail
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Réinitialiser avec un token
          </Typography>
          <Stack spacing={1}>
            <TextField size="small" label="Token reçu par email"
              value={resetToken} onChange={(e) => setResetToken(e.target.value)} />
            <TextField size="small" type="password" label="Nouveau mot de passe"
              value={resetNewPwd} onChange={(e) => setResetNewPwd(e.target.value)} />
            <Box>
              <Button variant="outlined" onClick={doReset}
                disabled={!resetToken.trim() || !resetNewPwd.trim()}>
                Réinitialiser
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <VerifiedIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Vérifier un email
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField size="small" label="Code de vérification"
              value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} />
            <Button variant="outlined" onClick={verifyEmail}
              disabled={!email.trim() || !verifyCode.trim()}>
              Valider
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <PasswordIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Changer le mot de passe
          </Typography>
          <Stack spacing={1}>
            <TextField size="small" type="password" label="Ancien mot de passe"
              value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} />
            <TextField size="small" type="password" label="Nouveau mot de passe"
              value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
            <Box>
              <Button variant="outlined" onClick={changePwd}
                disabled={!email.trim() || !oldPwd || !newPwd}>
                Modifier
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default { list: AuthAdmin };
